import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Point tunnel: rings along Z that advance toward the camera (flight effect).
 * Each ring's radius is modulated by frequency (around the circumference) and
 * by the bass; beats expand it.
 */
export class Tunnel extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aAngle;     // 0..2PI
        attribute float aRing;      // 0..1 (position along the tunnel)
        attribute float aFreqIndex; // 0..1
        uniform float uTime, uBass, uTreble, uBeat, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vDepth;
        varying float vMag;
        varying vec3 vDir;
        ${ZONE_GLSL_COMMON}
        void main() {
          // When zone reaction is on, the tunnel is frozen — only the zones react.
          float gate = 1.0 - uZoneEnabled;
          float zlen = 42.0;
          float speed = 7.0;
          float d = mod(aRing * zlen + uTime * speed * gate, zlen);
          float z = d - zlen + 6.5;   // from far away (-) toward the camera (~6.5)
          vDepth = clamp((z + zlen - 6.5) / zlen, 0.0, 1.0);
          float mag = texture2D(uFreq, vec2(aFreqIndex, 0.5)).r;
          vMag = mag;
          float radius = 2.3
                       + (mag * 1.6 * uReactivity
                          + uBass * uBassPunch * 0.9
                          + sin(aAngle * 5.0 + uTime * 1.5) * 0.12) * gate;
          // Seed-driven beat punch (suppressed when zone reaction is on).
          float lobe = sin(aAngle * 3.0 + uSeed.x) * cos(aRing * 5.0 + uSeed.y);
          radius += lobe * uSeedPulse * uReactivity * 0.8;
          // Zone reaction: shapes ride (angle, depth) and dent the ring locally.
          vec3 zdir = zoneDirFromUV(vec2(aAngle / 6.2831853, vDepth));
          vDir = zdir;
          radius += zoneDisp(zdir) * 1.4;
          vec3 pos = vec3(cos(aAngle) * radius, sin(aAngle) * radius, z);
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float px = (4.0 + mag * 7.0 + uBeat * 4.0) * (10.0 / -mv.z);
          gl_PointSize = clamp(px, 1.5, 48.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble, uLevel;
        varying float vDepth;
        varying float vMag;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          // Fade at the ends (appears in the back, fades out as it passes).
          float fade = smoothstep(0.0, 0.15, vDepth) * smoothstep(1.0, 0.85, vDepth);
          float hue = fract(uHue + vDepth * 0.2 + vMag * 0.15 + uTreble * 0.1);
          vec3 col = hsv2rgb(vec3(hue, 0.8, 0.5 + vMag * 0.5));
          float alpha = core * fade * (uPointSolid > 0.5 ? 1.0 : (0.3 + vMag * 0.6));
          vec4 zc = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col * core), zc.rgb * core, zc.a);
          gl_FragColor = vec4(outc, max(alpha, zc.a * core * fade));
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    // The actual positions are computed in the shader -> avoid frustum culling.
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const d = params.density ?? 1
    const rings = Math.floor(160 * Math.sqrt(d))
    const perRing = Math.floor(90 * Math.sqrt(d))
    const count = rings * perRing
    const positions = new Float32Array(count * 3)
    const angle = new Float32Array(count)
    const ring = new Float32Array(count)
    const freqIndex = new Float32Array(count)

    let i = 0
    for (let r = 0; r < rings; r++) {
      for (let p = 0; p < perRing; p++) {
        const a = (p / perRing) * Math.PI * 2
        positions[i * 3 + 0] = 0
        positions[i * 3 + 1] = 0
        positions[i * 3 + 2] = 0
        angle[i] = a
        ring[i] = r / rings
        // Map the angle to the spectrum (the tunnel ripples by frequency).
        freqIndex[i] = Math.abs(Math.sin(a))
        i++
      }
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aAngle', new THREE.BufferAttribute(angle, 1))
    geo.setAttribute('aRing', new THREE.BufferAttribute(ring, 1))
    geo.setAttribute('aFreqIndex', new THREE.BufferAttribute(freqIndex, 1))
    geo.computeBoundingSphere()
  }
}
